"use client";

/**
 * CopyPausedBanner — top-of-app banner that fires when the user has
 * active copytrade issues to look at.
 *
 * Two severity levels:
 *   - RED (pause): one or more follows is sitting in `auto_paused_at`.
 *     No copies are firing for those pairs until the user resumes (or
 *     for insufficient_balance, until balance recovers).
 *   - YELLOW (warning): copies are STILL firing, but the trade-executor
 *     recently flagged that the computed copy size keeps landing below
 *     the $1 Polymarket minimum for this (user, leader) pair. No auto-
 *     pause — the user just gets a heads-up to fix config. Backend
 *     emits this from /api/copytrading/following based on a 24h Redis
 *     key set by trade-executor.worker.ts:416.
 *
 * If both exist simultaneously the red banner wins (higher severity).
 *
 * Per-event dismissal: clicking X stores ids in localStorage so the
 * user is not nagged again. For pauses the id is the pause timestamp
 * (a new pause re-shows). For warnings the id is the leader_id (one
 * dismiss per leader per session; a fresh 24h Redis cycle re-surfaces).
 *
 * Sources of pauses we surface here:
 *   - `loss_threshold`             (manual resume required)
 *   - `insufficient_balance`       (auto-resumes on deposit/redeem)
 *   - `below_min_size_consistent`  (manual resume required)
 *   - `max_open_positions`         (auto-resumes when a position closes)
 *
 * Sources of warnings we surface here:
 *   - `copy_size_too_small`        (config tweak takes effect on next trade)
 */

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { FiPauseCircle, FiAlertTriangle, FiX } from "react-icons/fi";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";
import { STATIC_ASSETS } from "@/lib/cdn";

// Lazy-load the copy-settings modal — only needed when the user clicks
// "Fix settings" on the banner. Opens IN-PLACE on whatever page the user
// is on (the banner is global), so we never navigate away.
const CopyTraderModal = dynamic(
  () => import("../traders/components/CopyTraderModal"),
  { ssr: false },
);

// Locale-bound translate fn signature (matches useT()'s return).
type TFn = (key: string, vars?: Record<string, string | number>) => string;

interface PausedLeader {
  leader_id: string;
  polymarket_username: string;
  custom_label?: string | null;
  // Needed to open the copy-settings modal in-place (no navigation):
  // CopyTraderModal fetches the full 22-col config by wallet address.
  polymarket_address: string;
  profile_image_url: string | null;
  auto_paused_at: string;
  auto_pause_reason: string | null;
  // Per-reason context snapshot the backend captured when it flipped
  // `auto_paused_at`. Lets the banner show the actual metric
  // (-22.4% loss vs -20% cap, $2.40 balance, 10/10 positions, ...)
  // instead of a generic "loss threshold" label. Optional — older
  // pause rows won't have it, and the banner falls back to the
  // legacy reason text in that case.
  auto_pause_context?: {
    currentLossPct?: number | null;
    lossCapPct?: number | null;
    balance?: number | null;
    requiredUsd?: number | null;
    openCount?: number | null;
    cap?: number | null;
    attemptedSizeUsd?: number | null;
    minSizeUsd?: number | null;
    maxSizeUsd?: number | null;
  } | null;
}

interface WarnedLeader {
  leader_id: string;
  polymarket_username: string;
  custom_label?: string | null;
  polymarket_address: string;
  profile_image_url: string | null;
  reason: string;
}

const DISMISS_KEY = "frenflow-copy-paused-dismissed";
const WARN_DISMISS_KEY = "frenflow-copy-warning-dismissed";

function readDismissed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeDismissed(set: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    // Cap the stored list at 100 entries so a chronic auto-pauser doesn't
    // grow localStorage indefinitely. Oldest entries roll off first.
    const arr = Array.from(set).slice(-100);
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify(arr));
  } catch {
    /* private browsing or quota exceeded — silently ignore */
  }
}

function reasonText(t: TFn, reason: string | null): string {
  switch (reason) {
    case "insufficient_balance":
      return t("copyBanner.reason.insufficientBalance");
    case "loss_threshold":
      return t("copyBanner.reason.lossThreshold");
    case "profit_target":
      return t("copyBanner.reason.profitTarget");
    case "below_min_size_consistent":
      return t("copyBanner.reason.belowMin");
    case "above_max_size_consistent":
      return t("copyBanner.reason.aboveMax");
    case "max_open_positions":
      return t("copyBanner.reason.maxPositions");
    default:
      return t("copyBanner.reason.system");
  }
}

// Inline reason label with the actual metric pulled from
// `auto_pause_context`. Banner has room for one line, so the format
// trades the toast's hero block for a compact "value (cap N)" parens
// pattern. Falls back to `reasonText()` when context is missing so the
// banner still reads correctly for legacy pause rows.
function reasonHeadline(t: TFn, reason: string | null, ctx: PausedLeader["auto_pause_context"]): string {
  if (!ctx) return reasonText(t, reason);
  switch (reason) {
    case "loss_threshold": {
      // The word "loss" already implies negative — keeping the minus
      // sign in addition reads as `-22.4% loss (cap -20%)` which stacks
      // dashes against the em-dash separator before it. Use plain
      // positive magnitudes here.
      if (typeof ctx.currentLossPct === "number") {
        const pct = Math.abs(ctx.currentLossPct).toFixed(1);
        return typeof ctx.lossCapPct === "number"
          ? t("copyBanner.headline.loss", { pct, cap: Math.abs(ctx.lossCapPct).toFixed(0) })
          : t("copyBanner.headline.lossNoCap", { pct });
      }
      return reasonText(t, reason);
    }
    case "insufficient_balance": {
      if (typeof ctx.balance === "number") {
        const bal = ctx.balance.toFixed(2);
        return typeof ctx.requiredUsd === "number"
          ? t("copyBanner.headline.balance", { bal, need: ctx.requiredUsd.toFixed(2) })
          : t("copyBanner.headline.balanceNoNeed", { bal });
      }
      return reasonText(t, reason);
    }
    case "max_open_positions": {
      if (typeof ctx.openCount === "number" && typeof ctx.cap === "number") {
        return t("copyBanner.headline.positions", { open: ctx.openCount, cap: ctx.cap });
      }
      return reasonText(t, reason);
    }
    case "below_min_size_consistent": {
      if (typeof ctx.attemptedSizeUsd === "number") {
        const attempted = ctx.attemptedSizeUsd.toFixed(2);
        return typeof ctx.minSizeUsd === "number"
          ? t("copyBanner.headline.attemptedMin", { attempted, min: ctx.minSizeUsd.toFixed(0) })
          : t("copyBanner.headline.attempted", { attempted });
      }
      return reasonText(t, reason);
    }
    case "above_max_size_consistent": {
      if (typeof ctx.attemptedSizeUsd === "number") {
        const attempted = ctx.attemptedSizeUsd.toFixed(0);
        return typeof ctx.maxSizeUsd === "number"
          ? t("copyBanner.headline.attemptedCap", { attempted, cap: ctx.maxSizeUsd.toFixed(0) })
          : t("copyBanner.headline.attempted", { attempted });
      }
      return reasonText(t, reason);
    }
    default:
      return reasonText(t, reason);
  }
}

function warningText(t: TFn, reason: string): string {
  switch (reason) {
    case "copy_size_too_small":
      return t("copyBanner.warn.sizeTooSmall");
    case "high_slippage":
      return t("copyBanner.warn.highSlippage");
    default:
      return t("copyBanner.warn.default");
  }
}

// Short relative-time string for the banner. Doesn't need full
// internationalisation here — banner copy is one line, English only
// for now, and `Xm/Xh/Xd ago` reads cleanly across locales.
function timeAgo(t: TFn, iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return t("copyBanner.time.justNow");
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return t("copyBanner.time.justNow");
  if (mins < 60) return t("copyBanner.time.minutes", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("copyBanner.time.hours", { n: hrs });
  const days = Math.floor(hrs / 24);
  return t("copyBanner.time.days", { n: days });
}

function readWarnDismissed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(WARN_DISMISS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeWarnDismissed(set: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    const arr = Array.from(set).slice(-100);
    window.localStorage.setItem(WARN_DISMISS_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

// "What to do" instruction shown at the top of the settings modal when it
// is opened from the CTA — maps the skip/pause reason to a concrete action.
function fixInstruction(t: TFn, reason: string | null | undefined): string {
  switch (reason) {
    case "high_slippage":
      return t("copyBanner.fix.highSlippage");
    case "copy_size_too_small":
      return t("copyBanner.fix.sizeTooSmall");
    case "insufficient_balance":
      return t("copyBanner.fix.insufficientBalance");
    case "loss_threshold":
      return t("copyBanner.fix.lossThreshold");
    case "profit_target":
      return t("copyBanner.fix.profitTarget");
    case "below_min_size_consistent":
      return t("copyBanner.fix.belowMin");
    case "above_max_size_consistent":
      return t("copyBanner.fix.aboveMax");
    case "max_open_positions":
      return t("copyBanner.fix.maxPositions");
    default:
      return t("copyBanner.fix.default");
  }
}

export default function CopyPausedBanner() {
  const { isLoggedIn } = useAuth();
  const t = useT();
  // Copy-settings modal opened in-place from the CTA (no navigation).
  const [fixModal, setFixModal] = useState<
    { leaderId: string; trader: { username: string; wallet: string; avatar: string }; notice: string } | null
  >(null);
  // Bumped after a successful save so the banner re-fetches and clears the
  // row the user just fixed without waiting for the 60s poll.
  const [refreshTick, setRefreshTick] = useState(0);
  const [paused, setPaused] = useState<PausedLeader[]>([]);
  const [warned, setWarned] = useState<WarnedLeader[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());
  const [warnDismissed, setWarnDismissed] = useState<Set<string>>(() => readWarnDismissed());

  useEffect(() => {
    if (!isLoggedIn) {
      setPaused([]);
      setWarned([]);
      return;
    }
    let cancelled = false;
    const fetchPaused = async () => {
      try {
        const r = await authFetch("/api/copytrading/following");
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const followingArr: any[] = data?.following || [];

        const pausedRows: PausedLeader[] = followingArr
          .filter((f: any) => f?.auto_paused_at != null)
          .map((f: any) => ({
            leader_id: String(f.leader_id),
            polymarket_username: String(f.polymarket_username || ""),
            custom_label: f.custom_label ?? null,
            polymarket_address: String(f.polymarket_address || ""),
            profile_image_url: f.profile_image_url ?? null,
            auto_paused_at: String(f.auto_paused_at),
            auto_pause_reason: f.auto_pause_reason ?? null,
            auto_pause_context: f.auto_pause_context ?? null,
          }));

        // Warning rows: follows with a `warning` field set by the
        // backend (Redis 24h key from trade-executor MIN_SIZE path).
        // Exclude follows that are also paused — pauses win.
        const warnedRows: WarnedLeader[] = followingArr
          .filter(
            (f: any) =>
              f?.warning?.reason && f?.auto_paused_at == null,
          )
          .map((f: any) => ({
            leader_id: String(f.leader_id),
            polymarket_username: String(f.polymarket_username || ""),
            custom_label: f.custom_label ?? null,
            polymarket_address: String(f.polymarket_address || ""),
            profile_image_url: f.profile_image_url ?? null,
            reason: String(f.warning.reason),
          }));

        if (!cancelled) {
          setPaused(pausedRows);
          setWarned(warnedRows);
        }
      } catch {
        /* silent — banner just doesn't render */
      }
    };
    fetchPaused();
    // Re-check every 60s so newly-paused / newly-warned rows surface
    // without a hard reload, and so resumed / fixed rows disappear.
    const id = setInterval(fetchPaused, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isLoggedIn, refreshTick]);

  // The pause-id is just the timestamp — unique per (leader, pause event).
  // When the user dismisses, we mark THIS specific pause as acked; a new
  // pause on the same leader (different timestamp) will resurface the
  // banner, which is the desired UX.
  const visiblePaused = useMemo(
    () => paused.filter((p) => !dismissed.has(`${p.leader_id}:${p.auto_paused_at}`)),
    [paused, dismissed],
  );
  const visibleWarned = useMemo(
    () => warned.filter((w) => !warnDismissed.has(w.leader_id)),
    [warned, warnDismissed],
  );

  if (!isLoggedIn) return null;
  // Red banner (pauses) takes precedence over yellow banner (warnings).
  // We don't render both simultaneously — keeps the header clean.
  const visible = visiblePaused;
  if (visible.length === 0 && visibleWarned.length === 0) return null;
  const isWarning = visible.length === 0 && visibleWarned.length > 0;

  // Single-leader: full sentence with username + reason + relative
  // time so the user immediately knows what / when.
  // Multi-leader: list up to 3 usernames inline (banner has space) so
  // it's actionable at a glance — no need to click through to see who.
  // Beyond 3 we fall back to the count to keep the line under-wraps.
  // polymarket_username can be a raw "0x…-id" fallback when the leader has no
  // handle — show @handle for real handles, a truncated address otherwise.
  const leaderName = (u?: string | null, label?: string | null): string => {
    if (label && label.trim()) return label.trim();
    if (!u) return "trader";
    if (!u.startsWith("0x")) return `@${u}`;
    const a = u.split("-")[0];
    return a.length >= 10 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
  };
  const headline = (() => {
    if (isWarning) {
      if (visibleWarned.length === 1) {
        const w = visibleWarned[0];
        return t("copyBanner.skip.one", {
          leader: leaderName(w.polymarket_username, w.custom_label),
          reason: warningText(t, w.reason),
        });
      }
      if (visibleWarned.length <= 3) {
        const names = visibleWarned.map((w) => `${leaderName(w.polymarket_username, w.custom_label)}`).join(", ");
        return t("copyBanner.skip.few", { count: visibleWarned.length, names });
      }
      return t("copyBanner.skip.many", { count: visibleWarned.length });
    }
    if (visible.length === 1) {
      const p = visible[0];
      return t("copyBanner.paused.one", {
        leader: leaderName(p.polymarket_username, p.custom_label),
        reason: reasonHeadline(t, p.auto_pause_reason, p.auto_pause_context),
        time: timeAgo(t, p.auto_paused_at),
      });
    }
    if (visible.length <= 3) {
      const names = visible.map((p) => `${leaderName(p.polymarket_username, p.custom_label)}`).join(", ");
      return t("copyBanner.paused.few", { count: visible.length, names });
    }
    return t("copyBanner.paused.many", { count: visible.length });
  })();

  // CTA target. For a SINGLE affected leader we open the copy-settings
  // modal in-place (no navigation) — the banner is global, so this works on
  // any page. We need the leader's wallet to fetch the full config. If the
  // wallet is missing (older payload) or there are multiple leaders, fall
  // back to navigating to the Traders tab.
  const fixTarget = (() => {
    const one = isWarning
      ? (visibleWarned.length === 1 ? visibleWarned[0] : null)
      : (visible.length === 1 ? visible[0] : null);
    if (!one || !one.polymarket_address) return null;
    // Reason drives the modal's "what to do" note: warnings carry `reason`,
    // pauses carry `auto_pause_reason`.
    const reason = "reason" in one ? one.reason : one.auto_pause_reason;
    return {
      leaderId: one.leader_id,
      trader: {
        username: one.polymarket_username
          || `${one.polymarket_address.slice(0, 6)}...${one.polymarket_address.slice(-4)}`,
        wallet: one.polymarket_address,
        avatar: one.profile_image_url || STATIC_ASSETS.defaultAvatar,
      },
      notice: fixInstruction(t, reason),
    };
  })();

  const handleDismiss = () => {
    if (isWarning) {
      const next = new Set(warnDismissed);
      for (const w of visibleWarned) next.add(w.leader_id);
      writeWarnDismissed(next);
      setWarnDismissed(next);
    } else {
      const next = new Set(dismissed);
      for (const p of visible) next.add(`${p.leader_id}:${p.auto_paused_at}`);
      writeDismissed(next);
      setDismissed(next);
    }
  };

  // Theme: red for actual pauses (action required, copies are NOT
  // firing), yellow for warnings (copies still firing but skipping
  // for one follow — heads-up, not a stop). Same layout, swap color
  // tokens + icon so it stays familiar.
  const theme = isWarning
    ? {
        border: 'border-yellow-500/60',
        bg: 'bg-yellow-500/20',
        icon: <FiAlertTriangle className="h-4 w-4 flex-shrink-0 text-yellow-500" />,
        link: 'text-yellow-700 hover:bg-yellow-500/10 dark:text-yellow-400',
        dismissHover: 'hover:bg-yellow-500/10',
      }
    : {
        border: 'border-red-500/60',
        bg: 'bg-red-500/20',
        icon: <FiPauseCircle className="h-4 w-4 flex-shrink-0 text-red-500" />,
        link: 'text-red-600 hover:bg-red-500/10 dark:text-red-400',
        dismissHover: 'hover:bg-red-500/10',
      };

  return (
    <>
    <div className={`sticky top-12 lg:top-16 z-[90] w-full border-b ${theme.border} ${theme.bg} backdrop-blur-md`}>
      <div className="mx-auto flex max-w-[1400px] flex-col gap-1.5 px-4 py-2 sm:grid sm:grid-cols-[1fr_auto] sm:items-center sm:gap-3">
        <div className="flex min-w-0 items-center gap-2 sm:justify-center">
          {theme.icon}
          <span className="text-xs text-gray-800 dark:text-gray-100 sm:line-clamp-1 sm:text-sm">
            {headline}
          </span>
        </div>
        <div className="flex items-center justify-end gap-3">
          {fixTarget ? (
            <button
              type="button"
              onClick={() => setFixModal(fixTarget)}
              className={`rounded px-2 py-1 text-xs font-medium ${theme.link} sm:text-sm`}
            >
              {isWarning ? t("copyBanner.cta.fix") : t("copyBanner.cta.manage")}
            </button>
          ) : (
            <Link
              href="/copytrading?tab=traders"
              className={`rounded px-2 py-1 text-xs font-medium ${theme.link} sm:text-sm`}
            >
              {isWarning ? t("copyBanner.cta.fix") : t("copyBanner.cta.manage")}
            </Link>
          )}
          <button
            onClick={handleDismiss}
            aria-label={t("copyBanner.dismiss")}
            className={`rounded p-1 text-gray-500 transition-colors ${theme.dismissHover} hover:text-gray-800 dark:hover:text-white`}
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
    {fixModal && (
      <CopyTraderModal
        isOpen
        onClose={() => setFixModal(null)}
        trader={fixModal.trader}
        leaderId={fixModal.leaderId}
        notice={fixModal.notice}
        onSuccess={() => {
          setFixModal(null);
          setRefreshTick((n) => n + 1);
        }}
      />
    )}
    </>
  );
}
